import React, { useEffect, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, Tabs, Tab } from "@mui/material";
import { IniInfo } from "system/types/interfaces";
import { ApplicabilityRuleBuilder } from "../../subjectViewPage/applicabilityTab/ApplicabilityRuleBuilder";
import { useDebouncedCallback } from "use-debounce";

interface AddSystemConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: IniInfo, type: "config" | "comment") => void;
  initialRule: IniInfo | null;
  initialComment: string | null;
  mode: "add" | "edit";
}

const AddSystemConfigModal: React.FC<AddSystemConfigModalProps> = ({ open, onClose, onSubmit, mode, initialRule, initialComment }) => {
  const [tabIndex, setTabIndex] = useState<0 | 1>(0);
  const [localRule, setLocalRule] = useState<IniInfo>({ name: "", value: "", applicability: "", delimiter: "", inlineComment: "" });
  const [nameError, setNameError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [comment, setComment] = useState<string>("");
  const nameRef = useRef<HTMLDivElement>(null);
  const commentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialRule) {
      setLocalRule(initialRule);
      setTabIndex(0);
    } else {
      setTabIndex(1);
      setComment(initialComment || "");
    }
  }, [initialComment, initialRule]);

  const sanitizeConfigName = (name: string): string => {
    if (!name) return "";
    let sanitized = name.replace(/[^a-zA-Z0-9\-_]/g, "-");
    sanitized = sanitized.replace(/^-+|-+$/g, "");
    return sanitized;
  };

  const debouncedSanitizeAndValidate = useDebouncedCallback((value: string) => {
    const san = sanitizeConfigName(value);
    setLocalRule((prev) => ({ ...prev, name: san }));
    const err = validateName(san);
    setNameError(err);
  }, 1000);

  const validateName = (value: string): string | null => {
    const regex = /^[a-zA-Z0-9][a-zA-Z0-9_-]*[a-zA-Z0-9]$/;
    if (!regex.test(value)) return "Name must begin/end with [a-zA-Z0-9], allow -/_.";
    if (mode === "add" && value === "Standard") return "Standard is reserved.";
    return null;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setLocalRule((prev) => ({ ...prev, name: newName }));
    debouncedSanitizeAndValidate(newName);
  };

  const handleBuilderChange = (result: {
    finalValue: string;
    finalDelimiter: string;
    comment: string;
    fullSyntax: string;
    isValid: boolean;
  }) => {
    if (!result.isValid) {
      setIsValid(result.isValid);
      return;
    }
    setIsValid(result.isValid);

    if (
      localRule.value === result.finalValue &&
      localRule.delimiter === result.finalDelimiter &&
      localRule.inlineComment === result.comment
    ) {
      return;
    }

    setLocalRule((prev) => ({
      ...prev,
      value: result.finalValue,
      delimiter: result.finalDelimiter,
      inlineComment: result.comment,
    }));
  };

  const handleSubmit = () => {
    if (tabIndex === 0) {
      onSubmit(localRule, "config");
    } else {
      const newlineMatch = comment.match(/^(\n*)/);
      const leadingNewlines = newlineMatch ? newlineMatch[0].split("\n").length - 1 : 0;
      const contentWithoutNewlines = comment.replace(/^(\n*)/, "").trim();
      const finalComment = contentWithoutNewlines.startsWith(";") ? contentWithoutNewlines : `; ${contentWithoutNewlines}`;
      onSubmit(
        {
          name: `New-Comment-${Date.now()}`,
          value: finalComment,
          delimiter: "",
          inlineComment: null,
          emptyLinesBefore: leadingNewlines,
          isComment: true,
        },
        "comment"
      );
    }
    onClose();
  };

  useEffect(() => {
    if (open) {
      if (tabIndex === 0) nameRef.current?.focus();
      else if (tabIndex === 1) commentRef.current?.focus();
    }
  }, [open, tabIndex]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#ffffff" }}>
        <Typography variant="inherit" fontWeight="bold" color="#ffffff">
          {tabIndex === 0 ? "Add Line" : "Add Row Comment"}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: "#1b1a19",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Tabs value={mode === "edit" ? tabIndex : tabIndex} onChange={(_, value) => mode === "add" && setTabIndex(value)} centered>
          <Tab label="Configuration" disabled={mode === "edit" && tabIndex === 1} />
          <Tab label="Comment" />
        </Tabs>

        {tabIndex === 0 && (
          <>
            <TextField
              label="Config Name"
              value={localRule.name}
              onChange={handleNameChange}
              inputRef={nameRef}
              error={!!nameError}
              helperText={nameError}
            />
            <ApplicabilityRuleBuilder
              initialName={localRule?.name || ""}
              initialDelimiter={localRule.delimiter}
              initialValue={localRule.value}
              initialComment={localRule.inlineComment || ""}
              isUpdating={true}
              onChange={handleBuilderChange}
              shouldDisplayNameValue={true}
            />
          </>
        )}
        {tabIndex === 1 && (
          <TextField
            fullWidth
            label="Add Comment"
            multiline
            rows={7}
            placeholder="Add your comment here"
            value={comment}
            inputProps={{ style: { color: "#4caf50" } }}
            inputRef={commentRef}
            onChange={(e) => setComment(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#1b1a19" }}>
        <Button variant="outlined" onClick={onClose} sx={{ color: "#ffffff", borderColor: "#757575" }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={tabIndex === 0 ? !!nameError || !isValid : false}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { AddSystemConfigModal };
